import { motion } from 'framer-motion'
import {Building, Award, Users, TrendingUp} from 'lucide-react'
import { useEstatisticas } from '../hooks/useStatistics'
import { APPS_SCRIPT_CONFIG } from '../utils/constants'
import { Link } from 'react-router-dom'

const Portfolio = () => {
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hook para buscar estatísticas da API
  const { stats, loading } = useEstatisticas(APPS_SCRIPT_CONFIG);

  const successStats = [
    {
      icon: Award,
      number: stats?.orcamentosFechados || 5,
      label: 'Orçamentos Fechados',
      isNumeric: true,
      loading: loading && !stats,
      description: 'Reconhecimento por excelência técnica'
    },
    {
      icon: Users,
      number: stats?.percentualSatisfacao || 100,
      label: 'Clientes Satisfeitos',      
      isNumeric: true,
      suffix: '%',
      loading: loading && !stats,
      description: 'Técnicos capacitados e atendimento dedicado'
    },
    {
      icon: TrendingUp,
      number: 35,
      label: 'Economia Média',
      description: 'Redução no consumo energético',
      isNumeric: true,
      suffix: '%',
      loading: false
    },
    {
      icon: Building,
      number: 1000000,
      label: 'M² Climatizados',
      description: 'Área total de projetos realizados',
      isNumeric: true,
      formatter: (num: number) => `${(num / 1000000).toFixed(0)}M+`,
      loading: false
    }
  ]

  return (
    <div className="pt-20">
      {/* Skip to main content link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg z-50 focus:z-50"
      >
        Pular para o conteúdo principal
      </a>

      {/* Hero Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800 text-white"
        aria-labelledby="portfolio-hero-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 id="portfolio-hero-title" className="text-5xl md:text-6xl font-bold mb-6">
              Nosso Portfólio
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Projetos que transformam ambientes e superam expectativas. 
              Conheça nossos casos de sucesso.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Success Stats */}
      <section 
        id="main-content"
        className="py-16 bg-white"
        aria-labelledby="success-stats-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="success-stats-title" className="sr-only">
            Estatísticas de sucesso da empresa
          </h2>
          
          {/* Status de loading para leitores de tela */}
          {loading && (
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              Carregando estatísticas da empresa...
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {successStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`text-center ${stat.loading ? 'animate-pulse' : ''}`}
                role="img"
                aria-label={`${stat.label}: ${
                  stat.loading 
                    ? 'carregando' 
                    : stat.formatter 
                      ? stat.formatter(stat.number)
                      : stat.isNumeric 
                        ? `${stat.number}${stat.suffix || ''}` 
                        : stat.number
                }. ${stat.description}`}
              >
                <div 
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  aria-hidden="true"
                >
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
                
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {stat.loading ? (
                    <>
                      <span 
                        className="inline-block bg-gray-200 rounded w-16 h-8"
                        aria-hidden="true"
                      ></span>
                      <span className="sr-only">Carregando estatística</span>
                    </>
                  ) : (
                    <span aria-hidden="true">
                      {stat.formatter ? (
                        stat.formatter(stat.number)
                      ) : stat.isNumeric ? (
                        <>
                          {stat.number.toLocaleString('pt-BR')}
                          {stat.suffix}
                        </>
                      ) : (
                        stat.number
                      )}
                    </span>
                  )}
                </div>
                
                <h3 className="text-gray-900 font-medium mb-1" id={`stat-label-${index}`}>
                  {stat.label}
                </h3>
                <p 
                  className="text-gray-600 text-sm" 
                  aria-describedby={`stat-label-${index}`}
                >
                  {stat.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Informação adicional para leitores de tela */}
          <div className="sr-only">
            {stats ? (
              <p aria-live="polite">
                Estatísticas atualizadas com dados reais da API: 
                {stats.orcamentosFechados} orçamentos fechados, 
                {stats.percentualSatisfacao}% de satisfação dos clientes.
              </p>
            ) : (
              <p aria-live="polite">
                Exibindo estatísticas padrão enquanto os dados são carregados.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Future Projects Section - Placeholder for commented sections */}
      <section 
        className="py-16 bg-gray-50"
        aria-labelledby="future-projects-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 id="future-projects-title" className="text-3xl font-bold text-gray-900 mb-6">
              Projetos em Destaque
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Trabalhamos com uma ampla variedade de projetos, desde residenciais até 
              grandes complexos industriais, sempre com foco na excelência e inovação.
            </p>
            
            {/* Lista de tipos de projetos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {[
                {
                  title: 'Projetos Comerciais',
                  description: 'Shopping centers, hotéis e edifícios corporativos com soluções integradas',
                  icon: Building
                },
                {
                  title: 'Indústrias',
                  description: 'Frigoríficos, farmacêuticas e fábricas com controle rigoroso de temperatura',
                  icon: TrendingUp
                },
                {
                  title: 'Hospitais e Clínicas',
                  description: 'Ambientes hospitalares com controle de pureza e climatização especializada',
                  icon: Award
                }
              ].map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-lg"
                >
                  <div 
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4"
                    aria-hidden="true"
                  >
                    <project.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {project.title}
                  </h3>
                  <p className="text-gray-600">
                    {project.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800 text-white"
        aria-labelledby="cta-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 id="cta-title" className="text-4xl font-bold mb-4">
              Seu Projeto Pode Ser o Próximo Sucesso
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Entre em contato conosco e receba um orçamento personalizado para suas 
              necessidades de refrigeração e climatização.
            </p>
            <Link 
              to="/budget"
              onClick={handleLinkClick} 
              className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center focus:ring-4 focus:ring-white focus:outline-none"
              aria-describedby="cta-description"
            >
              Solicitar Orçamento
            </Link>
            <div id="cta-description" className="sr-only">
              Clique para ir para a página de orçamento e solicitar um orçamento personalizado 
              para seus projetos de refrigeração e climatização
            </div>
          </motion.div>
        </div>
      </section>

      {/* Informações adicionais para SEO e acessibilidade */}
      <div className="sr-only">
        <h2>Informações adicionais sobre nosso portfólio</h2>
        <p>
          A Tech Glacial possui vasta experiência em projetos de refrigeração e climatização, 
          atendendo desde residências até grandes complexos industriais. Nossos projetos 
          incluem shopping centers, hospitais, indústrias farmacêuticas, frigoríficos, 
          hotéis e edifícios comerciais em todo o Brasil.
        </p>
        <p>
          Utilizamos tecnologia de ponta com foco em eficiência energética e sustentabilidade. 
          Todos os projetos são executados por equipe técnica especializada e seguem 
          rigorosamente as normas técnicas e ambientais vigentes.
        </p>
      </div>
    </div>
  )
}

export default Portfolio